const {
  getFundClassProductType,
  getProductListingPath,
} = require('./product-paths.cjs')

const SITE_URL = 'https://www.harborcapital.com/'
const ORGANIZATION_ID = `${SITE_URL}#organization`
const WEBSITE_ID = `${SITE_URL}#website`
const ORGANIZATION_LOGO_URL =
  'https://images.contentstack.io/v3/assets/blt56fad3014e7f45aa/blt6fe4d2f3bcc010fc/6616aebfcbc2fb4d84818018/HarborLogoPeopleEntry.png'
const ORGANIZATION_SAME_AS = [
  'https://www.linkedin.com/company/harbor-capital-advisors',
  'https://x.com/HarborCapital',
  'https://www.youtube.com/@harborcapital',
  'https://www.facebook.com/harborcapital/',
  'https://www.instagram.com/harborcap/',
  'https://www.reddit.com/u/harborcapital',
  'https://www.tiktok.com/@harborcapital',
  'https://adviserinfo.sec.gov/firm/summary/113042',
]
const ORGANIZATION_DESCRIPTION =
  'Founded in 1983, Harbor Capital Advisors, Inc. has more than 40 years of experience providing access to active investment strategies and investment managers.'

const HTML_ENTITIES = {
  '&amp;': '&',
  '&apos;': "'",
  '&#39;': "'",
  '&gt;': '>',
  '&lt;': '<',
  '&nbsp;': ' ',
  '&quot;': '"',
}

const toArray = (value) =>
  Array.isArray(value) ? value : value == null ? [] : [value]

const normalizeText = (value) => {
  if (typeof value !== 'string') return ''

  return value
    .replace(/<[^>]*>/g, ' ')
    .replace(
      /&(amp|apos|#39|gt|lt|nbsp|quot);/g,
      (entity) => HTML_ENTITIES[entity] || entity
    )
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/\s+/g, ' ')
    .trim()
}

const extractText = (value) => {
  if (typeof value === 'string') return normalizeText(value)
  if (Array.isArray(value)) {
    return value.map(extractText).filter(Boolean).join(' ').trim()
  }
  if (!value || typeof value !== 'object') return ''
  if (typeof value.text === 'string') return normalizeText(value.text)
  if (typeof value.html === 'string') return normalizeText(value.html)

  return extractText(value.children || value.content)
}

const normalizePathname = (value = '/') => {
  let pathname = String(value || '/').trim()
  try {
    pathname = new URL(pathname, SITE_URL).pathname
  } catch {
    // Fall through to path normalization for malformed CMS values.
  }

  pathname = pathname.replace(/\/+/g, '/')
  if (!pathname.startsWith('/')) pathname = `/${pathname}`
  return pathname === '/' ? '/' : `${pathname.replace(/\/$/, '')}/`
}

const absoluteUrl = (value) => {
  if (!value) return ''
  try {
    return new URL(value, SITE_URL).toString()
  } catch {
    return ''
  }
}

const canonicalUrl = (pathname) => absoluteUrl(normalizePathname(pathname))

const pruneEmpty = (value) => {
  if (Array.isArray(value)) {
    const values = value.map(pruneEmpty).filter((item) => item != null)
    return values.length ? values : null
  }
  if (value && typeof value === 'object') {
    const result = Object.entries(value).reduce((accumulator, [key, child]) => {
      const next = pruneEmpty(child)
      if (next != null) accumulator[key] = next
      return accumulator
    }, {})
    return Object.keys(result).length ? result : null
  }
  if (value === false || value === 0) return value
  if (typeof value === 'string') return value.trim() || null
  return value == null ? null : value
}

const safeJsonStringify = (value) =>
  JSON.stringify(pruneEmpty(value))
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')

const getOpenGraphImage = (openGraph = {}) =>
  absoluteUrl(
    openGraph?.og_image?.url ||
      openGraph?.og_image?.localAsset?.publicURL ||
      openGraph?.og_image?.localAsset?.childImageSharp?.resize?.src
  )

const getPageDescription = (page = {}) => {
  const hero = toArray(page?.hero).find(Boolean) || {}
  return (
    extractText(page?.seo?.meta_description) ||
    extractText(page?.short_description) ||
    extractText(page?.hero_text) ||
    extractText(hero?.suite_description) ||
    extractText(hero?.description) ||
    extractText(hero?.subheadline) ||
    extractText(page?.description)
  )
}

const getPageTitle = (page = {}) =>
  extractText(page?.seo?.meta_title) || extractText(page?.title)

const getSiteNodes = () => [
  {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'Harbor Capital Advisors',
    legalName: 'Harbor Capital Advisors, Inc.',
    description: ORGANIZATION_DESCRIPTION,
    url: SITE_URL,
    foundingDate: '1983',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '111 South Wacker Drive, 34th Floor',
      addressLocality: 'Chicago',
      addressRegion: 'IL',
      postalCode: '60606-4302',
      addressCountry: 'US',
    },
    logo: {
      '@type': 'ImageObject',
      url: ORGANIZATION_LOGO_URL,
    },
    identifier: [
      {
        '@type': 'PropertyValue',
        propertyID: 'CRD',
        value: '113042',
      },
      {
        '@type': 'PropertyValue',
        propertyID: 'SEC',
        value: '801-60367',
      },
    ],
    subjectOf: {
      '@type': 'WebPage',
      url: `${SITE_URL}about-us/leadership/`,
      name: 'Harbor Capital leadership',
    },
    sameAs: ORGANIZATION_SAME_AS,
  },
  {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_URL,
    name: 'Harbor Capital',
    alternateName: 'Harbor Capital Advisors',
    publisher: { '@id': ORGANIZATION_ID },
    inLanguage: 'en-US',
  },
]

const buildBreadcrumbNode = (items, pathname) => {
  const canonical = canonicalUrl(pathname)
  const normalizedItems = toArray(items)
    .map((item) => ({
      name: normalizeText(item?.name),
      url: absoluteUrl(item?.path || item?.url),
    }))
    .filter((item) => item.name && item.url)

  return {
    '@type': 'BreadcrumbList',
    '@id': `${canonical}#breadcrumb`,
    itemListElement: normalizedItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }
}

const getProductBreadcrumbItems = ({ product, fundClass, pathname } = {}) => {
  const productType =
    getFundClassProductType(fundClass) || product?.product_type
  const shareClassTitle = fundClass?.share_class?.[0]?.title
  const listingPath = getProductListingPath({
    productType,
    shareClassTitle,
  })
  const listingName =
    String(productType || '').toUpperCase() === 'ETF' ? 'ETFs' : 'Mutual Funds'

  return [
    { name: 'Home', path: '/' },
    { name: listingName, path: listingPath },
    { name: product?.title || fundClass?.ticker, path: pathname },
  ]
}

const getCollectionBreadcrumbItems = ({ title, pathname } = {}) => [
  { name: 'Home', path: '/' },
  { name: title || 'Products', path: pathname },
]

const formatPercentageText = (value) => {
  if (value == null || String(value).trim() === '') return ''
  const text = String(value).trim()
  return text.endsWith('%') ? text : `${text}%`
}

const buildIdentifierNodes = (
  fundClass = {},
  { includeProductFacts = true } = {}
) => {
  const productFacts = fundClass?.etf_product_facts?.[0] || {}
  return [
    ['Ticker', fundClass?.ticker],
    ['CUSIP', includeProductFacts ? productFacts?.cusip : ''],
    ['ISIN', includeProductFacts ? productFacts?.isin : ''],
  ]
    .filter(([, value]) => normalizeText(value))
    .map(([propertyID, value]) => ({
      '@type': 'PropertyValue',
      propertyID,
      value: normalizeText(value),
    }))
}

const normalizeQuestionAnswerItems = (data) => {
  const candidates = [
    data?.qa_item,
    data?.question_and_answer?.qa_item,
    data?.q_a_block?.qa_item,
    Array.isArray(data) ? data : null,
  ]
  const items = candidates.find((candidate) => Array.isArray(candidate)) || []

  return items
    .map((item) => ({
      question: extractText(item?.question || item?.q),
      answer: extractText(item?.answer || item?.a),
    }))
    .filter((item) => item.question && item.answer)
}

const getProductQuestionAnswerItems = (product = {}) => {
  const uniqueItems = new Map()
  toArray(product?.product_tabs)
    .flatMap((tab) => normalizeQuestionAnswerItems(tab?.q_a_block))
    .forEach((item) => {
      const key = `${item.question.toLowerCase()}\u0000${item.answer.toLowerCase()}`
      if (!uniqueItems.has(key)) uniqueItems.set(key, item)
    })
  return Array.from(uniqueItems.values())
}

const hasProductSeoTickerMismatch = (product = {}, fundClass = {}) => {
  const routeTicker = normalizeText(fundClass?.ticker).toUpperCase()
  const authoredTicker = extractText(product?.seo?.meta_title).match(
    /\(([A-Z][A-Z0-9.-]{1,9})\)\s*$/
  )?.[1]
  return Boolean(
    routeTicker && authoredTicker && routeTicker !== authoredTicker
  )
}

const hasProductDataMismatch = (product = {}, fundClass = {}) => {
  if (hasProductSeoTickerMismatch(product, fundClass)) return true

  const routeTicker = normalizeText(fundClass?.ticker).toUpperCase()
  const productFactsTicker = normalizeText(
    fundClass?.etf_product_facts?.[0]?.intraday_ticker
  ).toUpperCase()
  return Boolean(
    routeTicker && productFactsTicker && routeTicker !== productFactsTicker
  )
}

const slugify = (value) =>
  normalizeText(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const getProductSubadviserNodes = (product = {}, canonical = '') => {
  const firms = toArray(product?.product_subadviser)
    .flatMap((relationship) => toArray(relationship?.subadviser_firm))
    .filter((firm) => normalizeText(firm?.title))
  const uniqueFirms = new Map()
  firms.forEach((firm) => {
    const name = normalizeText(firm.title)
    const key = canonicalUrl(firm?.url) || name.toLowerCase()
    if (!uniqueFirms.has(key)) uniqueFirms.set(key, firm)
  })

  const organizations = []
  const services = []
  Array.from(uniqueFirms.values()).forEach((firm) => {
    const name = normalizeText(firm?.title)
    const isHarbor = /harbor capital advisors(?:,? inc\.?)?/i.test(name)
    const firmUrl = firm?.url ? canonicalUrl(firm.url) : ''
    const organizationId = isHarbor
      ? ORGANIZATION_ID
      : firmUrl
        ? `${firmUrl}#organization`
        : `${SITE_URL}#organization-${slugify(name)}`
    const serviceId = `${canonical}#subadvisory-service-${slugify(name)}`

    if (!isHarbor) {
      organizations.push({
        '@type': 'Organization',
        '@id': organizationId,
        name,
        url: firmUrl,
        description: extractText(firm?.description),
      })
    }
    services.push({
      '@type': 'Service',
      '@id': serviceId,
      name: `${name} investment subadvisory services`,
      serviceType: 'Investment subadvisory management',
      provider: { '@id': organizationId },
    })
  })

  return {
    names: Array.from(uniqueFirms.values()).map((firm) =>
      normalizeText(firm?.title)
    ),
    organizations,
    services,
  }
}

const buildFundAdditionalProperties = (fundClass = {}, subadvisers = []) => {
  const properties = [
    ['Inception date', 'inceptionDate', fundClass?.inception_date, ''],
    [
      'Gross expense ratio',
      'grossExpenseRatio',
      fundClass?.expense_ratio?.gross,
      '%',
    ],
    [
      'Net expense ratio',
      'netExpenseRatio',
      fundClass?.expense_ratio?.net,
      '%',
    ],
    [
      'Adjusted expense ratio',
      'adjustedExpenseRatio',
      fundClass?.expense_ratio?.adjusted,
      '%',
    ],
    ...subadvisers.map((name) => ['Subadvisor', 'subadvisor', name, '']),
  ]

  return properties
    .filter(([, , value]) => normalizeText(String(value == null ? '' : value)))
    .map(([name, propertyID, value, unitText]) => ({
      '@type': 'PropertyValue',
      name,
      propertyID,
      value: normalizeText(String(value)),
      unitText,
    }))
}

const getProductDescription = (product, fundClass) => {
  if (!hasProductSeoTickerMismatch(product, fundClass)) {
    return getPageDescription(product)
  }

  const title = extractText(product?.title)
  const ticker = normalizeText(fundClass?.ticker).toUpperCase()
  return title && ticker
    ? `${title} (${ticker}) investment product information from Harbor Capital Advisors.`
    : ''
}

const buildProductGraph = ({
  product = {},
  fundClass = {},
  pathname,
  breadcrumbItems,
} = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`
  const fundId = `${canonical}#investment-fund`
  const description = getProductDescription(product, fundClass)
  const pageTitle = hasProductSeoTickerMismatch(product, fundClass)
    ? extractText(product?.title)
    : getPageTitle(product)
  const productType =
    getFundClassProductType(fundClass) || product?.product_type || ''
  const productTypeLabel =
    String(productType).toUpperCase() === 'ETF' ? 'ETF' : 'Mutual Fund'
  const hasDataMismatch = hasProductDataMismatch(product, fundClass)
  const feeParts = [
    !hasDataMismatch && fundClass?.expense_ratio?.gross
      ? `Gross expense ratio: ${formatPercentageText(
          fundClass.expense_ratio.gross
        )}`
      : '',
    !hasDataMismatch && fundClass?.expense_ratio?.net
      ? `Net expense ratio: ${formatPercentageText(fundClass.expense_ratio.net)}`
      : '',
  ].filter(Boolean)
  const subadviserGraph = hasDataMismatch
    ? { names: [], organizations: [], services: [] }
    : getProductSubadviserNodes(product, canonical)
  const questions = getProductQuestionAnswerItems(product)
  const faqId = `${canonical}#faq`
  const breadcrumb = buildBreadcrumbNode(
    breadcrumbItems ||
      getProductBreadcrumbItems({ product, fundClass, pathname }),
    pathname
  )
  const webpage = {
    '@type': 'WebPage',
    '@id': webpageId,
    url: canonical,
    name: pageTitle,
    description,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': fundId },
    mainEntity: { '@id': fundId },
    breadcrumb: { '@id': breadcrumb['@id'] },
    dateModified: product?.updated_at,
    inLanguage: 'en-US',
    primaryImageOfPage: getOpenGraphImage(product?.open_graph)
      ? {
          '@type': 'ImageObject',
          url: getOpenGraphImage(product.open_graph),
        }
      : null,
    hasPart: questions.length ? { '@id': faqId } : null,
  }
  const investmentFund = {
    // Deliberately excludes Product: Google applies its merchant-listing
    // validator to any Product node and rejects it without offers/review/
    // aggregateRating, none of which apply to a fund. InvestmentFund and
    // FinancialProduct descend from Service, so the validator no longer runs.
    '@type': ['InvestmentFund', 'FinancialProduct'],
    '@id': fundId,
    name: extractText(product?.title),
    alternateName: fundClass?.ticker
      ? `${extractText(product?.title)} (${normalizeText(fundClass.ticker)})`
      : '',
    description,
    url: canonical,
    mainEntityOfPage: { '@id': webpageId },
    provider: { '@id': ORGANIZATION_ID },
    category: productTypeLabel,
    identifier: buildIdentifierNodes(fundClass, {
      includeProductFacts: !hasDataMismatch,
    }),
    feesAndCommissionsSpecification: feeParts.join('; '),
    additionalProperty: hasDataMismatch
      ? null
      : buildFundAdditionalProperties(fundClass, subadviserGraph.names),
    isRelatedTo: subadviserGraph.services.map((service) => ({
      '@id': service['@id'],
    })),
    image: getOpenGraphImage(product?.open_graph),
    subjectOf:
      !hasDataMismatch &&
      absoluteUrl(
        product?.documents?.summary_prospectus?.localAsset?.publicURL ||
          product?.documents?.prospectus?.localAsset?.publicURL
      )
        ? {
            '@type': 'DigitalDocument',
            name: product?.documents?.summary_prospectus
              ? 'Summary prospectus'
              : 'Prospectus',
            url: absoluteUrl(
              product?.documents?.summary_prospectus?.localAsset?.publicURL ||
                product?.documents?.prospectus?.localAsset?.publicURL
            ),
          }
        : null,
  }
  const faq = questions.length
    ? {
        '@type': 'FAQPage',
        '@id': faqId,
        url: `${canonical}#QuestionsAndAnswers`,
        isPartOf: { '@id': webpageId },
        mainEntity: questions.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }
    : null

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': [
      ...getSiteNodes(),
      webpage,
      investmentFund,
      breadcrumb,
      faq,
      ...subadviserGraph.organizations,
      ...subadviserGraph.services,
    ],
  })
}

const normalizeCollectionProduct = (product, index) => {
  const path = product?.path || product?.url
  const url = canonicalUrl(path)
  const name = extractText(product?.title || product?.name)
  if (!url || !name) return null

  return {
    '@type': 'ListItem',
    position: index + 1,
    url,
    item: {
      '@type': 'InvestmentFund',
      '@id': `${canonicalUrl(path)}#investment-fund`,
      name,
      description:
        extractText(product?.short_description) ||
        extractText(product?.short_suite_description),
      url,
      provider: { '@id': ORGANIZATION_ID },
    },
  }
}

const buildCollectionGraph = ({
  page = {},
  products = [],
  pathname,
  breadcrumbItems,
} = {}) => {
  const canonical = canonicalUrl(pathname)
  const webpageId = `${canonical}#webpage`
  const itemListId = `${canonical}#item-list`
  const listItems = products
    .map(normalizeCollectionProduct)
    .filter(Boolean)
    .map((item, index) => ({ ...item, position: index + 1 }))
  const breadcrumb = buildBreadcrumbNode(
    breadcrumbItems ||
      getCollectionBreadcrumbItems({ title: page?.title, pathname }),
    pathname
  )

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@graph': [
      ...getSiteNodes(),
      {
        '@type': 'CollectionPage',
        '@id': webpageId,
        url: canonical,
        name: getPageTitle(page),
        description: getPageDescription(page),
        isPartOf: { '@id': WEBSITE_ID },
        mainEntity: { '@id': itemListId },
        breadcrumb: { '@id': breadcrumb['@id'] },
        dateModified: page?.updated_at,
        inLanguage: 'en-US',
      },
      {
        '@type': 'ItemList',
        '@id': itemListId,
        name: `${extractText(page?.title) || 'Products'} product list`,
        numberOfItems: listItems.length,
        itemListOrder: 'https://schema.org/ItemListOrderAscending',
        itemListElement: listItems,
      },
      breadcrumb,
    ],
  })
}

module.exports = {
  ORGANIZATION_ID,
  SITE_URL,
  WEBSITE_ID,
  absoluteUrl,
  buildBreadcrumbNode,
  buildCollectionGraph,
  buildProductGraph,
  canonicalUrl,
  extractText,
  getCollectionBreadcrumbItems,
  getOpenGraphImage,
  getPageDescription,
  getPageTitle,
  getProductBreadcrumbItems,
  getSiteNodes,
  hasProductDataMismatch,
  normalizePathname,
  normalizeQuestionAnswerItems,
  normalizeText,
  pruneEmpty,
  safeJsonStringify,
}
